import {
  compareMoney,
  serializeMoney,
  subtractMoney,
  sumMoney,
} from '@erp/shared-utils';
import type { CashReceiptSettlement } from '@erp/rpc';

export interface ProjectedSettlement extends CashReceiptSettlement {
  /** Stated totals of the picked (not yet saved) remittances. */
  pickedTotal: string;
  /** Receipt amount minus the projected linked total; null when unknown. */
  remaining: string | null;
}

/**
 * The settlement a receipt would have if the picked remittances were linked:
 * the server's rule (stated totals of active links against the receipt
 * amount, to the cent) applied to the current links plus the picks, so the
 * running sum shows the state a save will produce. Indeterminate stays
 * indeterminate: picks can't supply a missing receipt amount or a linked
 * remittance's missing total.
 */
export function projectSettlement(params: {
  receiptAmount: string | null;
  current: CashReceiptSettlement;
  pickedTotals: readonly string[];
}): ProjectedSettlement {
  const { receiptAmount, current, pickedTotals } = params;
  const pickedTotal = serializeMoney(sumMoney(pickedTotals));
  const linkedTotal = serializeMoney(
    sumMoney([current.linkedTotal, pickedTotal]),
  );
  const activeLinkCount = current.activeLinkCount + pickedTotals.length;
  const remaining =
    receiptAmount == null
      ? null
      : serializeMoney(subtractMoney(receiptAmount, linkedTotal));
  const base = { linkedTotal, activeLinkCount, pickedTotal, remaining };

  if (receiptAmount == null || current.state === 'indeterminate') {
    return { ...base, state: 'indeterminate' };
  }
  if (activeLinkCount === 0) return { ...base, state: 'under' };
  const cmp = compareMoney(linkedTotal, receiptAmount);
  return { ...base, state: cmp === 0 ? 'settled' : cmp < 0 ? 'under' : 'over' };
}
